import {
  EMPTY_ARRAY,
  EMPTY_STRING,
  type FormOpenedInNewTabOption,
  getKeys,
  localStorageGetTyped,
  localStorageSetTyped,
  RegrelloLocalStorageKey,
} from "@regrello/core-utils";
import type { WorkflowScheduleStatus } from "@regrello/graphql-api";
import type { RegrelloIconName } from "@regrello/ui-core";
import { atom } from "jotai";
import { atomWithReducer } from "jotai/utils";
import Cookies from "js-cookie";

import {
  RegrelloSessionStorageKey,
  sessionStorageGetTyped,
  sessionStorageRemoveTyped,
  sessionStorageSetTyped,
} from "../utils/getFromSessionStorage";

// Types
// =====

export const IS_AUTHENTICATED_COOKIE_KEY = "is_authenticated";

// Atoms
// =====

/**
 * The ID of the blueprint that is currently being used. This is used to display the blueprint
 * details in the blueprint menu.
 */
export const blueprintIdForUseBlueprintDialogAtom = atom<number | undefined>(undefined);

export const breadCrumbStateAtom =
  atom<
    Array<{
      name: string;
      url?: string;
    }>
  >(EMPTY_ARRAY);

/**
 * The current version number of the home-table data. Data should be reloaded when this changes.
 */
export const homeTableDataLoadVersionAtom = atom<number>(0);

/**
 * Whether the app is waiting for auth0 authentication to finish. This is necessary because auth0
 * api is not a proper async call. The api doesn't provide a way for us to know exactly when the
 * authentication is finished.
 */
export const isWaitingForAuth0AuthenticationAtom = atomWithReducer<boolean, boolean>(
  sessionStorageGetTyped(RegrelloSessionStorageKey.IS_WAITING_FOR_AUTH0_AUTHENTICATION) === "true",
  (_prevValue, newValue: boolean) => {
    // (hchen): Store this in session storage because a navigation happens after Auth0
    // authentication and it can't be disabled.
    if (newValue == null || !newValue) {
      sessionStorageRemoveTyped(RegrelloSessionStorageKey.IS_WAITING_FOR_AUTH0_AUTHENTICATION);
      return false;
    }
    // eslint-disable-next-line lingui/no-unlocalized-strings
    sessionStorageSetTyped(RegrelloSessionStorageKey.IS_WAITING_FOR_AUTH0_AUTHENTICATION, "true");
    return newValue;
  },
);

/**
 * Options for how the global create workflow dialog should be opened.
 *
 * - A value of `open` will open the creation dialog with no permission constraints.
 * - A value of `openDisableCreateFromScratch` will open the creation dialog, but disable the ability
 *   for the user to create a workflow from scratch. This is useful when the user can start a workflow
 *   from any of their shared blueprints, but _not_ create one from scratch (i.e., without a template).
 * - A value of `closed` will prevent the dialog from appearing.
 */
export type AddWorkflowDialogOpenAtomOptions = "open" | "openDisableCreateFromScratch" | "closed";

/**
 * Whether the 'Add Workflow Dialog' is currently open. Refer to {@see AddWorkflowDialogOpenAtomOptions}
 * for more information about the dialog's possible states, and this atom's possible values.
 */
// eslint-disable-next-line lingui/no-unlocalized-strings
export const isAddWorkflowDialogOpenAtom = atom<AddWorkflowDialogOpenAtomOptions>("closed");

/** Whether the user is logged in. */
export const isAuthenticatedAtom = atomWithReducer<boolean, boolean>(
  Cookies.get(IS_AUTHENTICATED_COOKIE_KEY) === "true",
  (_prevValue, newValue: boolean) => {
    if (newValue) {
      // eslint-disable-next-line lingui/no-unlocalized-strings
      Cookies.set(IS_AUTHENTICATED_COOKIE_KEY, "true");
    } else {
      Cookies.remove(IS_AUTHENTICATED_COOKIE_KEY);
    }
    return newValue;
  },
);

export const isCreateUserDialogOpenAtom = atom(false);
export const isCreateTagDialogOpenAtom = atom(false);
export const isCreateTeamDialogOpenAtom = atom(false);
export const isCreateRoleDialogOpenAtom = atom(false);
export const isCreateWorkflowDialogOpenAtom = atom(false);
export const isCreateWorkflowTemplateDialogOpenAtom = atom(false);
export const isCreateWorkflowTemplateFromScratchDialogOpenAtom = atom(false);
export const isDetailDialogOpenAtom = atom(false);
export const isGrantAccessToBlueprintDialogOpenAtom = atom(false);
export const isImportBlueprintDialogOpenAtom = atom(false);
export const isSidebarCollapsedAtom = getLocalStorageBooleanAtom(RegrelloLocalStorageKey.IS_SIDEBAR_COLLAPSED);

export const formOpenedInNewTabAtom = atomWithReducer<
  FormOpenedInNewTabOption | undefined,
  FormOpenedInNewTabOption | undefined
>(
  localStorageGetTyped(RegrelloLocalStorageKey.FORM_CREATED_VIA_FORM_SELECT_CREATE_OPTION),

  (_prevValue, newValue: FormOpenedInNewTabOption | undefined) => {
    if (newValue == null) {
      localStorage.removeItem(RegrelloLocalStorageKey.FORM_CREATED_VIA_FORM_SELECT_CREATE_OPTION);
      return undefined;
    }
    localStorageSetTyped(RegrelloLocalStorageKey.FORM_CREATED_VIA_FORM_SELECT_CREATE_OPTION, newValue);
    return newValue;
  },
);

/**
 * This atom allows all pages to show a update message such as "Saved at <timestamp>" message on the
 * page header.
 */
export const pageHeaderUpdatePropsAtom = atom<
  | {
      iconName: RegrelloIconName;
      isLoading: false;
      message: React.ReactNode;
    }
  | {
      isLoading: true;
      message: React.ReactNode;
    }
  | undefined
>(undefined);

/**
 * The atom allows the workflow page to show a banner indicating the current status of a workflow.
 */
export const pageHeaderWorkflowStatusAtom = atom<
  | {
      updatedBy: string;
      updatedAt: string;
      status: WorkflowScheduleStatus;
      endReason?: string;
    }
  | undefined
>(undefined);

/**
 * The id of the most recently added Action Item. This is useful for the nicety of highlighting the
 * Action Item that was just added if it belongs to the current screen the user is on.
 */
export const recentlyAddedTaskIdAtom = atom<number | undefined>(undefined);

/**
 * Invoke this to trigger a reload of home-table data.
 */
// TODO (clewis): Replace with GraphQL subscriptions?
export const reloadHomeTableDataAtom = atom(undefined, (get, set) => {
  set(homeTableDataLoadVersionAtom, get(homeTableDataLoadVersionAtom) + 1);
});

/**
 * The Action Item ID that is currently selected and being displayed in the Detail dialog.
 */
export const selectedTaskUuidAtom = atom<string | undefined>(undefined);

/**
 * The Action Item template ID that is currently selected and being displayed in the Detail dialog.
 */
export const selectedTaskTemplateIdAtom = atom<number | undefined>(undefined);

export type UserTheme = "light" | "dark" | "system";

/** The user's preferred visusal theme for their own Regrello UI. */
export const userThemeAtom = atomWithReducer<UserTheme, UserTheme>(
  localStorageGetTyped(RegrelloLocalStorageKey.USER_THEME) ?? "light", // Default is "light" theme.

  (_prevValue, newValue: UserTheme) => {
    localStorageSetTyped(RegrelloLocalStorageKey.USER_THEME, newValue);
    return newValue;
  },
);

/**
 * An atom to hold performance-report related context so that UserContext doesn't need to wrap
 * everything.
 */
export const fpsPerformanceReportUserAtom = atom<{ userEmail: string; tenantName: string }>({
  userEmail: EMPTY_STRING,
  tenantName: EMPTY_STRING,
});

/**
 * The user's preferred data tab layout for workflow pages.
 */
export type DataTabLayout = "continuous" | "stageAndTask";

const DataTabLayoutMap: Record<DataTabLayout, boolean> = {
  continuous: true,
  stageAndTask: true,
};

/** A typed array of all possible data tab layouts. */
export const DATA_TAB_LAYOUTS = getKeys(DataTabLayoutMap);

/**
 * The atom for the Data tab layout preference in a workflow or blueprint. This will primarily be
 * sourced from the URL but will fall back to this if no URL query param is present.
 */
export const dataTabLayoutAtom = atomWithReducer<DataTabLayout, DataTabLayout>(
  localStorageGetTyped(RegrelloLocalStorageKey.DATA_TAB_LAYOUT) ?? "stageAndTask",

  (_prevValue, newValue: DataTabLayout) => {
    localStorageSetTyped(RegrelloLocalStorageKey.DATA_TAB_LAYOUT, newValue);
    return newValue;
  },
);

function getLocalStorageBooleanAtom(localStorageKey: RegrelloLocalStorageKey) {
  return atomWithReducer<boolean, boolean>(
    localStorageGetTyped(localStorageKey) === "true",

    (_prevValue, newValue: boolean) => {
      // eslint-disable-next-line lingui/no-unlocalized-strings
      localStorageSetTyped(localStorageKey, newValue ? "true" : "false");
      return newValue;
    },
  );
}
